"use client"

import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react"

import { Button, cn, SofiaMark, Tooltip } from "@coval/ui"
import { AlertCircle, Check, ChevronDown, Sparkles } from "lucide-react"
import { Streamdown } from "streamdown"

import {
  extractSqlFromMarkdown,
  useActiveSqlHandoff,
} from "@/lib/coviSqlHandoff"

import { useCoviEntityLinkPlugins } from "../hooks/useCoviEntityLinkPlugins"
import { useSofiaSummaryReveal } from "../hooks/useSofiaSummaryReveal"
import type { CoviChatItem } from "../model/types"
import type { CoviSuggestedPrompt } from "../utils/suggestedPrompts"
import { CoviMessageFeedback } from "./CoviMessageFeedback"
import {
  CoviThinkingIndicator,
  SOFIA_AVATAR_SIZE,
} from "./CoviThinkingIndicator"
import { SOFIA_MARKDOWN_COMPONENTS } from "./SofiaMarkdownLink"
import { VoiceprintCaret } from "./VoiceprintCaret"

// Streamdown's own list defaults are `list-inside`, which drops wrapped lines
// back to the left margin. `list-outside` hangs the marker so continuation
// lines align with the first line's text.
const STREAMDOWN_CLASSES =
  "text-sm leading-relaxed text-coval-text [&_p]:mb-2 [&_p:last-child]:mb-0 " +
  "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-4 " +
  "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-5 " +
  "[&_li]:mb-1 [&_a]:text-coval-info-text [&_a]:underline [&_strong]:font-semibold " +
  "[&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] " +
  "[&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:font-semibold"

// Fenced code is rendered by CoviCodeBlock (a shaded, borderless, SQL-highlighted
// block) instead of Streamdown's default card.
const STREAMDOWN_COMPONENTS = SOFIA_MARKDOWN_COMPONENTS

export function IconButton({
  children,
  onClick,
  onPointerDown,
  title,
  titleOpen,
  label,
  disabled,
  variant = "ghost",
}: {
  children: ReactNode
  onClick: () => void
  onPointerDown?: (event: ReactPointerEvent<HTMLButtonElement>) => void
  /** Tooltip text shown on hover and keyboard focus. */
  title: string
  /** Holds the tooltip open, for feedback that has to outlive the click. */
  titleOpen?: boolean
  label: string
  disabled?: boolean
  variant?: "ghost" | "solid"
}) {
  return (
    <Tooltip content={title} forceOpen={titleOpen} triggerDisabled={disabled}>
      <button
        type="button"
        onClick={onClick}
        onPointerDown={onPointerDown}
        disabled={disabled}
        aria-label={label}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40",
          variant === "solid"
            ? "bg-coval-contrast text-coval-text-on-contrast hover:opacity-90"
            : "text-coval-text-muted hover:bg-surface-2 hover:text-coval-text",
        )}
      >
        {children}
      </button>
    </Tooltip>
  )
}

/**
 * The mark in the transcript's avatar column. It is centered on the first line
 * of the message beside it: the wrapper is one line box tall (`1lh` of the same
 * `text-sm leading-relaxed` the message body uses) and centers the mark in it,
 * so the alignment holds whatever the mark's size is.
 */
function SofiaAvatar() {
  return (
    <span className="flex h-[1lh] shrink-0 items-center text-sm leading-relaxed">
      <SofiaMark size={SOFIA_AVATAR_SIZE} />
    </span>
  )
}

export function WelcomeBlock({
  prompts,
  onSelectPrompt,
}: {
  prompts?: readonly CoviSuggestedPrompt[]
  onSelectPrompt: (prompt: string) => void
}) {
  return (
    <div className="space-y-4 pt-1">
      <div className="flex items-start gap-1.5">
        <SofiaAvatar />
        <p className="text-sm leading-relaxed text-coval-text">
          Hi! I&apos;m Sofia, the Coval agent. I can walk you through connecting
          an agent and running your first simulation, answer how-to questions
          from the docs, and help you decide what to test next.
        </p>
      </div>
      {prompts && prompts.length > 0 ? (
        <div
          role="group"
          className="ml-6.5 flex flex-wrap gap-2"
          aria-label="Suggested prompts"
        >
          {prompts.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelectPrompt(item.prompt)}
              className="max-w-full rounded-full border border-coval-border bg-surface-default px-3 py-1.5 text-left text-xs font-medium leading-snug text-coval-text transition-colors hover:bg-surface-1 focus-visible:outline-none"
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// Long user messages collapse to this height (px) until "Show more", matching
// ChatGPT's truncate-with-fade treatment of pasted walls of text.
const USER_MESSAGE_COLLAPSED_MAX_PX = 160

export function UserMessage({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const bubbleRef = useRef<HTMLDivElement>(null)

  // scrollHeight is the full content height even while clipped, so this stays
  // accurate regardless of the collapsed/expanded class. Re-measure on resize.
  useLayoutEffect(() => {
    const el = bubbleRef.current
    if (!el) {
      return
    }
    // New text starts collapsed so the toggle never lingers in a stale state.
    setExpanded(false)
    const measure = () =>
      setOverflowing(el.scrollHeight > USER_MESSAGE_COLLAPSED_MAX_PX + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text])

  const collapsed = overflowing && !expanded

  return (
    <div className="flex flex-col items-end">
      <div className="relative w-fit max-w-[85%]">
        <div
          ref={bubbleRef}
          className={cn(
            "whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-surface-1 px-3.5 py-2 text-sm text-coval-text",
            collapsed && "overflow-hidden",
          )}
          style={
            collapsed ? { maxHeight: USER_MESSAGE_COLLAPSED_MAX_PX } : undefined
          }
        >
          {text}
        </div>
        {collapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-2xl bg-gradient-to-t from-surface-1 to-transparent" />
        )}
      </div>
      {overflowing && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1 flex items-center gap-0.5 px-1 py-0.5 text-xs font-medium text-coval-text-muted transition-colors hover:text-coval-text"
        >
          {expanded ? "Show less" : "Show more"}
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 transition-transform",
              expanded && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  )
}

/** "Add to SQL" affordance shown under a Sofia reply when a form awaits SQL. */
function AddToSqlButton({ onUse }: { onUse: () => void }) {
  const [used, setUsed] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        onUse()
        setUsed(true)
      }}
      className="mt-2 inline-flex items-center gap-1 rounded-md border border-coval-border bg-surface-1 px-2 py-1 text-xs font-medium text-coval-text transition-colors hover:bg-surface-2"
    >
      {used ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Added
        </>
      ) : (
        <>
          <Sparkles className="h-3.5 w-3.5" />
          Add to SQL
        </>
      )}
    </button>
  )
}

export function ChatItemView({
  item,
  orgSlug,
  workspaceSlug,
  showCaret = false,
  onRetry,
  retryDisabled = false,
}: {
  item: CoviChatItem
  /** Active org slug — used to org-scope Sofia's inline entity deep links. */
  orgSlug: string | null
  workspaceSlug: string | null
  showCaret?: boolean
  onRetry?: () => void
  retryDisabled?: boolean
}) {
  // When another surface (e.g. the Custom SQL metric form) is waiting on a query,
  // offer to drop this message's SQL straight into it.
  const sqlHandoff = useActiveSqlHandoff()
  const entityLinkPlugins = useCoviEntityLinkPlugins(orgSlug, workspaceSlug)
  // A seeded page summary plays in the first time it is shown. Every other
  // assistant message settles immediately, so this is a no-op for them.
  const reveal = useSofiaSummaryReveal(
    item.id,
    item.kind === "assistant" ? item.text : "",
  )
  switch (item.kind) {
    case "user":
      return <UserMessage text={item.text} />
    case "assistant": {
      // The message occupies its own slot from the start, so while it is being
      // prepared the thinking indicator stands in it — the mark stays put and
      // the text grows out of the same row.
      if (reveal.preparing) {
        return <CoviThinkingIndicator visible />
      }
      const caret = showCaret || reveal.typing
      // Only on completed replies, and only while a form is waiting for SQL.
      const insertableSql =
        sqlHandoff && !caret ? extractSqlFromMarkdown(item.text) : null
      return (
        <div className="group/covi-msg flex items-start gap-1.5">
          {/* Once reply text starts streaming the mark resolves to the static
              logo — the pre-text CoviThinkingIndicator carries the animation. */}
          <SofiaAvatar />
          <div className={cn("min-w-0 flex-1 break-words", STREAMDOWN_CLASSES)}>
            <Streamdown
              components={STREAMDOWN_COMPONENTS}
              remarkPlugins={entityLinkPlugins}
            >
              {reveal.text}
            </Streamdown>
            <VoiceprintCaret active={caret} />
            {sqlHandoff && insertableSql && (
              <AddToSqlButton onUse={() => sqlHandoff.apply(item.text)} />
            )}
            {/* Feedback only on completed replies — not the streaming one. */}
            {!caret && (
              <CoviMessageFeedback
                messageId={item.id}
                messageLength={item.text.length}
              />
            )}
          </div>
        </div>
      )
    }
    case "analysis":
      return null
    case "tool":
      // Tool trace: tool items are grouped into the expandable CoviToolTrace
      // disclosure by the transcript map (groupTranscriptItems), so a lone tool
      // item never reaches this presentational switch. Kept for switch
      // exhaustiveness.
      return null
    case "error":
      return (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-lg border border-coval-danger-border bg-coval-danger-surface px-3 py-2 text-sm text-coval-danger-on-surface"
        >
          <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{item.text}</span>
          {onRetry ? (
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 text-base"
              onClick={onRetry}
              disabled={retryDisabled}
            >
              Retry
            </Button>
          ) : null}
        </div>
      )
    case "action":
      // Rendered by the transcript map as ConfirmActionCard — it needs the
      // executor and item-patch wiring this presentational switch doesn't have.
      return null
    default:
      return null
  }
}
